import React, { useMemo } from 'react';

import { IconButton } from '@material-ui/core';
import clsx from 'clsx';
import { format } from 'date-fns';
import type { ReactNodeLike } from 'prop-types';
import { useTranslation } from 'react-i18next';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import ContextMenu from 'components/ContextMenu/ContextMenu';
import Table from 'components/Table';
import type { AssetProcessingActivityReviewRow } from 'config/api/ropaReviews/ropaReviews.types';
import processing_activities_messages from 'messages/processing_activities_messages';
import type { AssetRopaCapabilities } from 'pages/ProcessingActivities/_shared/assetRopaCapabilities';
import { getDpiaFeatureDisabledTooltip, IS_DPIA_TEMPORARILY_DISABLED } from 'pages/ProcessingActivities/_shared/dpiaFeatureFlags';
import type { TableDataMappingRow } from 'types/Table';

import useStyles from '../ProcessingActivitiesAssetReviewsPage.styles';

type ReviewStatusMessageKey = keyof typeof processing_activities_messages.asset_ropa.review_status;

type Props = {
  capabilities: AssetRopaCapabilities;
  emptyStateContent?: ReactNodeLike;
  onAccept: (row: AssetProcessingActivityReviewRow) => void;
  onAssessDpia: (row: AssetProcessingActivityReviewRow) => void;
  onOpenAdminDpiaReview: (row: AssetProcessingActivityReviewRow) => void;
  onOpenAdminReview: (row: AssetProcessingActivityReviewRow) => void;
  onOpenOwnerEditor: (row: AssetProcessingActivityReviewRow, mode: 'view' | 'edit') => void;
  onRemove?: (row: AssetProcessingActivityReviewRow) => void;
  onRequestClarification: (row: AssetProcessingActivityReviewRow) => void;
  rows: AssetProcessingActivityReviewRow[];
};

const formatDate = (value: string) => {
  try {
    return format(new Date(value), 'yyyy-MM-dd');
  } catch {
    return value;
  }
};

const getReviewStatusDotClass = (status: AssetProcessingActivityReviewRow['reviewStatus'], styles: ReturnType<typeof useStyles>) => {
  switch (status) {
    case 'accepted':
      return styles.statusDotSuccess;
    case 'submitted':
      return styles.statusDotInfo;
    case 'clarification_requested':
      return styles.statusDotDanger;
    default:
      return styles.statusDotWarning;
  }
};

const getDpiaBadgeClass = (status: string, styles: ReturnType<typeof useStyles>) => {
  if (['indicated', 'required', 'dpia_required'].includes(status)) {
    return styles.dpiaBadgeDanger;
  }

  if (['ready_for_review', 'in_progress', 'clarification_requested'].includes(status)) {
    return styles.dpiaBadgeWarn;
  }

  if (['approved', 'not_required', 'dpia_not_required', 'not_indicated'].includes(status)) {
    return styles.dpiaBadgeSuccess;
  }

  return styles.dpiaBadgeInfo;
};

const getDpiaLabel = (status: string, t: (message: string | string[]) => string) => {
  const messageMap = processing_activities_messages.asset_ropa.dpia_status as Record<string, string[]>;
  const message = messageMap[status];
  return message ? t(message) : status.replace(/_/g, ' ');
};

const getUniqueRowMenuItems = (
  row: AssetProcessingActivityReviewRow,
  capabilities: AssetRopaCapabilities,
  handlers: {
    onAccept: (row: AssetProcessingActivityReviewRow) => void;
    onAssessDpia: (row: AssetProcessingActivityReviewRow) => void;
    onOpenAdminDpiaReview: (row: AssetProcessingActivityReviewRow) => void;
    onOpenOwnerEditor: (row: AssetProcessingActivityReviewRow, mode: 'view' | 'edit') => void;
    onRemove?: (row: AssetProcessingActivityReviewRow) => void;
    onRequestClarification: (row: AssetProcessingActivityReviewRow) => void;
  },
  t: (message: string | string[]) => string,
) => {
  const dpiaFeatureDisabledTooltip = IS_DPIA_TEMPORARILY_DISABLED ? getDpiaFeatureDisabledTooltip(t) : undefined;

  return [
    ...(capabilities.canOpenApaEditor && capabilities.canEditApa
      ? [
          {
            label: t(processing_activities_messages.asset_ropa.actions.edit),
            onClick: () => handlers.onOpenOwnerEditor(row, 'edit'),
          },
        ]
      : []),
    ...(capabilities.canRemoveProcessingActivity && handlers.onRemove
      ? [
          {
            label: t(processing_activities_messages.asset_ropa.owner_workspace.actions.remove_from_asset),
            onClick: () => handlers.onRemove?.(row),
          },
        ]
      : []),
    ...(capabilities.canAcceptRow && row.canAccept
      ? [
          {
            label: t(processing_activities_messages.asset_ropa.actions.accept),
            onClick: () => handlers.onAccept(row),
          },
        ]
      : []),
    ...(capabilities.canRequestClarificationFromList && row.canRequestClarification
      ? [
          {
            label: t(processing_activities_messages.asset_ropa.actions.request_clarification),
            onClick: () => handlers.onRequestClarification(row),
          },
        ]
      : []),
    ...(capabilities.canAssessDpia
      ? [
          {
            label: t(processing_activities_messages.asset_ropa.apa_review.assess_dpia),
            disabled: IS_DPIA_TEMPORARILY_DISABLED,
            disabledTooltip: dpiaFeatureDisabledTooltip,
            onClick: () => handlers.onAssessDpia(row),
          },
        ]
      : []),
    ...(capabilities.canOpenAdminDpiaReview && row.dpiaId
      ? [
          {
            label: t(processing_activities_messages.asset_ropa.apa_review.open_admin_dpia_review),
            disabled: IS_DPIA_TEMPORARILY_DISABLED,
            disabledTooltip: dpiaFeatureDisabledTooltip,
            onClick: () => handlers.onOpenAdminDpiaReview(row),
          },
        ]
      : []),
  ];
};

const AssetProcessingActivityReviewTable: React.FC<Props> = ({
  capabilities,
  emptyStateContent,
  onAccept,
  onAssessDpia,
  onOpenAdminDpiaReview,
  onOpenAdminReview,
  onOpenOwnerEditor,
  onRemove,
  onRequestClarification,
  rows,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();

  const showActionsColumn =
    (capabilities.canOpenApaEditor && capabilities.canEditApa) ||
    capabilities.canRemoveProcessingActivity ||
    capabilities.canAcceptRow ||
    capabilities.canRequestClarificationFromList ||
    capabilities.canAssessDpia ||
    capabilities.canOpenAdminDpiaReview;

  const dataMapping: TableDataMappingRow<AssetProcessingActivityReviewRow>[] = useMemo(() => {
    const columns: TableDataMappingRow<AssetProcessingActivityReviewRow>[] = [
      {
        id: 'processingActivityName',
        label: t(processing_activities_messages.asset_ropa.table.processing_activity),
        get: row => row.processingActivityName,
      },
      {
        id: 'reviewStatus',
        label: t(processing_activities_messages.asset_ropa.table.review_status),
        get: row => (
          <span className={styles.statusCell}>
            <span className={clsx(styles.statusDot, getReviewStatusDotClass(row.reviewStatus, styles))} />
            {t(processing_activities_messages.asset_ropa.review_status[row.reviewStatus as ReviewStatusMessageKey])}
          </span>
        ),
      },
      {
        id: 'activePurposesCount',
        label: t(processing_activities_messages.asset_ropa.table.active_purposes),
        get: row => row.activePurposesCount,
      },
      {
        id: 'dpiaDisplayStatus',
        label: t(processing_activities_messages.asset_ropa.table.dpia_status),
        get: row => (
          <span className={clsx(styles.dpiaBadge, getDpiaBadgeClass(row.dpiaDisplayStatus, styles))}>
            {getDpiaLabel(row.dpiaDisplayStatus, t)}
          </span>
        ),
      },
      {
        id: 'updatedAt',
        label: t(processing_activities_messages.asset_ropa.table.last_updated),
        get: row => formatDate(row.updatedAt),
      },
    ];

    if (showActionsColumn) {
      columns.push({
        id: 'actions',
        blockSorting: true,
        isAddon: true,
        label: '',
        get: row => {
          const menuItems = getUniqueRowMenuItems(
            row,
            capabilities,
            { onAccept, onAssessDpia, onOpenAdminDpiaReview, onOpenOwnerEditor, onRemove, onRequestClarification },
            t,
          );

          if (menuItems.length === 0) {
            return null;
          }

          return (
            <div className={styles.rowActions}>
              <ContextMenu elements={menuItems}>
                <IconButton aria-label={t(processing_activities_messages.asset_ropa.actions.row_menu)} size='small'>
                  <MoreIcon />
                </IconButton>
              </ContextMenu>
            </div>
          );
        },
      });
    }

    return columns;
  }, [
    capabilities,
    onAccept,
    onAssessDpia,
    onOpenAdminDpiaReview,
    onOpenOwnerEditor,
    onRemove,
    onRequestClarification,
    showActionsColumn,
    styles,
    t,
  ]);

  const handleRowClick = (_event: React.MouseEvent, row: AssetProcessingActivityReviewRow) => {
    if (capabilities.canOpenAdminReviewPage) {
      onOpenAdminReview(row);
      return;
    }

    if (capabilities.canOpenApaEditor) {
      onOpenOwnerEditor(row, 'view');
    }
  };

  return (
    <div
      aria-label={t(processing_activities_messages.asset_ropa.table.aria_label)}
      className={clsx(styles.tableScrollWrapper, { [styles.tableScrollWrapperWithActions]: showActionsColumn })}
      role='region'
    >
      <Table
        clickableRow
        data={rows}
        dataMapping={dataMapping}
        emptyStateContent={emptyStateContent}
        onRowClick={handleRowClick}
        refetching={false}
        rowAriaLabel={row => t(processing_activities_messages.asset_ropa.table.row_aria_label, { name: row.processingActivityName })}
      />
    </div>
  );
};

export default AssetProcessingActivityReviewTable;
